import React, { Suspense, useCallback, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import {
    MESSAGES,
    ORDER_BY_NUMBER_QUERY_PARAM,
    ORDER_BY_STRING_QUERY_PARAM,
} from 'src/modules/common/constants.ts';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MainLayout } from 'src/modules/common/layouts/MainLayout';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton';
import { composeFilter, Filter } from 'src/utils/composeFilter.ts';
import { initLocationState } from 'src/actuators/locationStateContext.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { NoData } from 'src/modules/common/components/NoData/NoData.tsx';
import clsx from 'clsx';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { getHomePageConfig, HomePageData, HomePageVariables } from 'src/actuators/home/HomePageMapping.ts';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon.tsx';
import { PATHS } from 'src/actuators/router.tsx';
import { ActuatorTypes } from 'src/actuators/home/components/ActuatorTypes.tsx';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { RegionsEnum } from 'src/generated/operations.ts';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { useLocationState } from 'src/actuators/user-input/useLocationState.ts';
import { ActuatorHomeInfo } from 'src/actuators/home/components/ActuatorHomeInfo.tsx';

export const HomePage = (): React.ReactNode => {
    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const [locationState] = useLocationState();

    const pageConfig = useMemo(() => getHomePageConfig(userSettings.currentRegion), [userSettings]);
    const [
        searchParams,
        filter,
        checkboxHandler,
        radioHandler,
        resetHandler,
        setSearchParams,
    ] = useFilterLogic(pageConfig.filterConfig);

    const updateUrlAndState = useCallback((newFilter: Filter) => {
        const newSearch = new URLSearchParams();
        Object.entries(newFilter).forEach(([name, value]) => {
            if (value.length > 0) {
                newSearch.set(name, JSON.stringify(value));
            }
        });
        newSearch.delete(ORDER_BY_NUMBER_QUERY_PARAM);
        newSearch.delete(ORDER_BY_STRING_QUERY_PARAM);
        const input = searchParams.get('input');
        if (input) {
            newSearch.set('input', input);
        }

        setSearchParams(newSearch, { replace: true, state: { ...initLocationState } });
    }, [setSearchParams, searchParams]);

    const isMandatorySelected = useMemo(() => {
        const keys = Object.keys(filter || {});
        return keys.length > 0;
    }, [filter]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="forward"
                disabled={!isMandatorySelected}
                state={locationState}
                to={{
                    pathname: `${PATHS.UserInput}`,
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const onChangeHandler = useCallback((detail: { name: string; value: string }) => {
        if (userSettings.currentRegion === RegionsEnum.Am) {
            updateUrlAndState(checkboxHandler(detail));
        } else {
            updateUrlAndState(radioHandler(detail));
        }
    }, [checkboxHandler, radioHandler, updateUrlAndState, userSettings]);

    const onResetHandler = useCallback(() => {
        resetHandler(pageConfig.filterConfig);
        updateUrlAndState({});
    }, [resetHandler, updateUrlAndState, pageConfig]);

    return (
        <Suspense fallback={<SkeletonMainLayout breadcrumbs={false}/>}>
            <Query<HomePageData, HomePageVariables>
                query={pageConfig.query}
                variables={{
                    variables: {
                        filter: composeFilter(
                            mandatoryFilterConfig.config,
                            mandatoryFilterConfig.values,
                            userSettings.currentLocale,
                        ),
                        locale: userSettings.currentLocale,
                        shoppingCenter: userSettings.shoppingCenter ?? '',
                    } }}
            >{({ data }) => {
                    if (data === undefined) {
                        return <SkeletonMainLayout/>;
                    }

                    return (
                        <>
                            <MainLayout>
                                <MainLayoutHeader
                                    titleId={MESSAGES.ActuatorsHomePageTitle}
                                    navigation={isMobile ? null : navigation}
                                    breadcrumbs={null}
                                />

                                {userSettings.currentRegion !== RegionsEnum.Am && (<ActuatorHomeInfo/>)}

                                {data ? (
                                    <div className={clsx({
                                        'vss-home-page': true,
                                    })}
                                    >
                                        <div className="vss-home-page__devices-container">
                                            <h2 className="vss-home-page__devices-title">
                                                <I18n messageId={MESSAGES.ActuatorsHomePageTypesTitle}/>
                                            </h2>

                                            <ActuatorTypes
                                                filterConfig={pageConfig.filterConfig}
                                                onChangeHandler={onChangeHandler}
                                                homePageData={data}
                                                filter={filter}
                                                currentRegion={userSettings.currentRegion}
                                            />

                                            {userSettings.currentRegion === RegionsEnum.Am && (
                                                <Button
                                                    className="vss-home-page__button"
                                                    kind="tertiary"
                                                    fullWidth={isMobile}
                                                    onClick={onResetHandler}
                                                >
                                                    <I18n messageId={MESSAGES.Reset}/>

                                                    <div className="vss-home-page__button-icon">
                                                        <ResetIcon/>
                                                    </div>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <NoData/>
                                )}
                            </MainLayout>

                            {isMobile && navigation}
                        </>
                    );
                }}
            </Query>
        </Suspense>
    );
};
